import { useEffect, useRef } from 'react'
import { useAttachmentStore, useSetAttachments, useSetChatInput } from '@renderer/chat'
import { useSetMessageMentions } from '@renderer/chat/components/chat/input/mentions/atoms'
import type { ConversationSkill } from '@renderer/chat/integrations/conversation-action'
import type { MentionableItem, MentionableShortcut } from '@renderer/chat/types'
import { pulseChatInput } from '../../hooks/use-chat-input-pulse'

/** What a new chat's composer opens holding, unsent. */
export interface ComposerDraft {
  /** The message text. It @-mentions each skill. Dropped once filled in: the entry's own draft keeps it from then on. */
  text?: string
  /** Attached as context alongside it. */
  context?: string
  /** What the context's chip reads. */
  contextLabel?: string
  skills?: Array<ConversationSkill>
}

/** A skill as the chat composer mentions it, so the turn invokes it. */
const skillMention = (skill: ConversationSkill): MentionableShortcut => ({
  type: 'shortcut',
  label: skill.name,
  value: skill.id,
  skillId: skill.id,
  agent_tools: null,
})

const mentionsSkill = (mention: MentionableItem, skillIds: ReadonlySet<string>) =>
  mention.type === 'shortcut' && mention.skillId != null && skillIds.has(mention.skillId)

/**
 * Fills a new chat's composer with `draft`. The text goes in once. The skills
 * and context live in the composer's shared state, which every view uses, so
 * they are added while `draft` is given and removed once it isn't. Give it only
 * while this chat is the top view and unsent, and they reach only this chat's
 * message, and survive the new-chat reset.
 */
export function useComposerDraft(draft: ComposerDraft | undefined, onTextFilled: () => void) {
  const setChatInput = useSetChatInput()
  const setMessageMentions = useSetMessageMentions()
  const setAttachments = useSetAttachments()
  const { addAttachment } = useAttachmentStore()
  const onTextFilledRef = useRef(onTextFilled)
  onTextFilledRef.current = onTextFilled

  const text = draft?.text
  const context = draft?.context
  const contextLabel = draft?.contextLabel
  const skillsKey = JSON.stringify(draft?.skills ?? [])

  // Deferred past the new-chat reset (which clears mentions and attachments) and the
  // sidepanel's draft restore (which sets the input for the new entry), or either undoes it.
  useEffect(() => {
    if (!text) {
      return
    }
    const timer = setTimeout(() => {
      setChatInput(text)
      onTextFilledRef.current()
      pulseChatInput({ focus: true })
    }, 0)
    return () => clearTimeout(timer)
  }, [text, setChatInput])

  useEffect(() => {
    const skills = JSON.parse(skillsKey) as Array<ConversationSkill>
    if (skills.length === 0 && !context) {
      return
    }
    const skillIds = new Set(skills.map((skill) => skill.id))
    const timer = setTimeout(() => {
      setMessageMentions((prev) => [
        ...prev.filter((mention) => !mentionsSkill(mention, skillIds)),
        ...skills.map(skillMention),
      ])
      if (context) {
        addAttachment({
          attachment: { type: 'clipboardText', text: context },
          ...(contextLabel ? { metadata: { label: contextLabel } } : {}),
        })
      }
    }, 0)
    return () => {
      clearTimeout(timer)
      setMessageMentions((prev) => prev.filter((mention) => !mentionsSkill(mention, skillIds)))
      if (context) {
        setAttachments((prev) =>
          prev.filter((item) => !(item.attachment?.type === 'clipboardText' && item.attachment.text === context)),
        )
      }
    }
  }, [context, contextLabel, skillsKey, setMessageMentions, setAttachments, addAttachment])
}
